import { Edit01, Trash01 } from "@untitledui/icons";
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import {
  useCreateProductUpdate,
  useDeleteProductUpdateThumbnail,
  useUpdateProductUpdate,
  useUploadProductUpdateThumbnail,
} from "@/api/admin/product-updates";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { TextAreaBase } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { FormField, FormItem } from "@/components/ui/form";
import { error, success } from "@/providers/ToastProvider";

const THUMBNAIL_MAX_WIDTH = 840;

type FormValues = {
  title: string;
  copy: string;
  linkText: string;
  linkUrl: string;
  isPublished: boolean;
  showAsPopup: boolean;
};

type ProductUpdateData = {
  id: string;
  title: string;
  copy: string;
  linkText?: string | null;
  linkUrl?: string | null;
  showAsPopup: boolean;
  publishedAt: string | null;
  thumbnailUrl: string | null;
};

export const CreateProductUpdateModal = () => {
  const [open, setOpen] = useState(false);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button>Add new Update</Button>
      <ProductUpdateModalContent mode="create" open={open} onClose={() => setOpen(false)} />
    </DialogTrigger>
  );
};

export const EditProductUpdateModal = ({ productUpdate }: { productUpdate: ProductUpdateData }) => {
  const [open, setOpen] = useState(false);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Edit" icon={Edit01} onClick={() => setOpen(true)} />
      <ProductUpdateModalContent mode="edit" open={open} onClose={() => setOpen(false)} productUpdate={productUpdate} />
    </DialogTrigger>
  );
};

type ModalContentProps =
  | { mode: "create"; open: boolean; onClose: () => void; productUpdate?: undefined }
  | { mode: "edit"; open: boolean; onClose: () => void; productUpdate: ProductUpdateData };

const toggleBold = (textarea: HTMLTextAreaElement, onChange: (value: string) => void) => {
  const { selectionStart, selectionEnd, value } = textarea;
  if (selectionStart === selectionEnd) return;

  const selected = value.slice(selectionStart, selectionEnd);
  const isBold = selected.startsWith("**") && selected.endsWith("**") && selected.length > 4;
  const wrappedBefore = selectionStart >= 2 && value.slice(selectionStart - 2, selectionStart) === "**";
  const wrappedAfter = value.slice(selectionEnd, selectionEnd + 2) === "**";
  const isWrapped = wrappedBefore && wrappedAfter;

  let next: string;
  let cursorStart: number;
  let cursorEnd: number;

  if (isBold) {
    const unwrapped = selected.slice(2, -2);
    next = value.slice(0, selectionStart) + unwrapped + value.slice(selectionEnd);
    cursorStart = selectionStart;
    cursorEnd = selectionStart + unwrapped.length;
  } else if (isWrapped) {
    next = value.slice(0, selectionStart - 2) + selected + value.slice(selectionEnd + 2);
    cursorStart = selectionStart - 2;
    cursorEnd = selectionEnd - 2;
  } else {
    next = `${value.slice(0, selectionStart)}**${selected}**${value.slice(selectionEnd)}`;
    cursorStart = selectionStart + 2;
    cursorEnd = selectionEnd + 2;
  }

  onChange(next);
  requestAnimationFrame(() => {
    textarea.setSelectionRange(cursorStart, cursorEnd);
  });
};

const handleCopyKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>, onChange: (value: string) => void) => {
  const textarea = e.currentTarget;

  if ((e.metaKey || e.ctrlKey) && e.key === "b") {
    e.preventDefault();
    toggleBold(textarea, onChange);
    return;
  }

  if (e.key === "Enter") {
    const { selectionStart, value } = textarea;
    const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
    const currentLine = value.slice(lineStart, selectionStart);

    if (/^- .+/.test(currentLine)) {
      e.preventDefault();
      if (currentLine.trim() === "-") {
        const next = `${value.slice(0, lineStart)}${value.slice(selectionStart)}`;
        onChange(next);
        requestAnimationFrame(() => textarea.setSelectionRange(lineStart, lineStart));
      } else {
        const insertion = "\n- ";
        const next = `${value.slice(0, selectionStart)}${insertion}${value.slice(selectionStart)}`;
        const cursor = selectionStart + insertion.length;
        onChange(next);
        requestAnimationFrame(() => textarea.setSelectionRange(cursor, cursor));
      }
    }
  }
};

type FormattedCopyFieldProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
};

const FormattedCopyField = ({ value, onChange, onBlur }: FormattedCopyFieldProps) => {
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => handleCopyKeyDown(e, onChange), [onChange]);

  return (
    <TextAreaBase
      placeholder="Enter copy text"
      rows={5}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
};

const ProductUpdateModalContent = ({ mode, open, onClose, productUpdate }: ModalContentProps) => {
  const isEdit = mode === "edit";
  const form = useForm<FormValues>({
    defaultValues: {
      title: productUpdate?.title ?? "",
      copy: productUpdate?.copy ?? "",
      linkText: productUpdate?.linkText ?? "",
      linkUrl: productUpdate?.linkUrl ?? "",
      isPublished: productUpdate ? productUpdate.publishedAt !== null : false,
      showAsPopup: productUpdate?.showAsPopup ?? false,
    },
  });

  const { mutateAsync: create } = useCreateProductUpdate();
  const { mutateAsync: update } = useUpdateProductUpdate();
  const { mutateAsync: uploadThumbnail } = useUploadProductUpdateThumbnail();
  const { mutateAsync: deleteThumbnail } = useDeleteProductUpdateThumbnail();

  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(productUpdate?.thumbnailUrl ?? null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on modal open/close
  useEffect(() => {
    form.reset({
      title: productUpdate?.title ?? "",
      copy: productUpdate?.copy ?? "",
      linkText: productUpdate?.linkText ?? "",
      linkUrl: productUpdate?.linkUrl ?? "",
      isPublished: productUpdate ? productUpdate.publishedAt !== null : false,
      showAsPopup: productUpdate?.showAsPopup ?? false,
    });
    setThumbnailPreview(productUpdate?.thumbnailUrl ?? null);
    setPendingFile(null);
  }, [open]);

  const validateAndSetFile = (file: File) => {
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      error("File must be an SVG, PNG, JPG or GIF image");
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    if (file.type === "image/svg+xml") {
      setPendingFile(file);
      setThumbnailPreview(objectUrl);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width > THUMBNAIL_MAX_WIDTH) {
        URL.revokeObjectURL(objectUrl);
        error(`Image width must be ${THUMBNAIL_MAX_WIDTH}px or smaller`);
        return;
      }
      setPendingFile(file);
      setThumbnailPreview(objectUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      error("Could not read image dimensions");
    };
    img.src = objectUrl;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    validateAndSetFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    validateAndSetFile(file);
  };

  const handleRemoveThumbnail = () => {
    setPendingFile(null);
    setThumbnailPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const payload = {
      title: values.title,
      copy: values.copy,
      linkText: values.linkText.trim() ? values.linkText.trim() : null,
      linkUrl: values.linkUrl.trim() ? values.linkUrl.trim() : null,
      isPublished: values.isPublished,
      showAsPopup: values.showAsPopup,
    };

    try {
      if (isEdit) {
        await update({ id: productUpdate.id, ...payload });

        if (pendingFile) {
          await uploadThumbnail({ id: productUpdate.id, file: pendingFile });
        } else if (!thumbnailPreview && productUpdate.thumbnailUrl) {
          await deleteThumbnail({ id: productUpdate.id });
        }

        success("Product update saved");
      } else {
        const created = await create(payload);
        if (pendingFile && created?.id) {
          await uploadThumbnail({ id: created.id, file: pendingFile });
        }
        success("Product update created");
      }
      onClose();
    } catch {
      error(isEdit ? "Failed to save product update" : "Failed to create product update");
    }
  };

  return (
    <ModalOverlay isDismissable>
      <Modal className="dark-mode">
        <Dialog>
          <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-[640px]">
            <div className="min-w-[640px] relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={onClose} theme="light" size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    {isEdit ? "Edit Product Update" : "Add new Update"}
                  </Heading>
                </div>
              </div>
              <div className="h-5 w-full" />
              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <FormField
                  control={form.control}
                  name="title"
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <Input isRequired label="Title" placeholder="Enter title" {...field} />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-medium text-secondary">Thumbnail</p>
                  {thumbnailPreview ? (
                    <div className="relative w-full rounded-lg overflow-hidden border border-primary">
                      <img src={thumbnailPreview} alt="Thumbnail preview" className="w-full h-auto object-contain" />
                      <button
                        type="button"
                        onClick={handleRemoveThumbnail}
                        className="absolute top-2 right-2 p-1 rounded-md bg-black/60 text-white hover:bg-black/80 transition-colors"
                      >
                        <Trash01 className="size-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={handleDrop}
                      className={`flex flex-col items-center justify-center gap-1 w-full py-4 rounded-lg border border-dashed transition-colors ${dragging ? "border-brand bg-brand/5" : "border-primary hover:border-brand"}`}
                    >
                      <p className="text-sm">
                        <span className="font-semibold text-brand cursor-pointer">Click to upload</span>
                        <span className="text-tertiary"> or drag and drop</span>
                      </p>
                      <p className="text-xs text-tertiary">SVG, PNG, JPG or GIF (max. {THUMBNAIL_MAX_WIDTH}px wide)</p>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/svg+xml,image/png,image/jpeg,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                <FormField
                  control={form.control}
                  name="copy"
                  rules={{ required: "Copy is required" }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1.5">
                      <Label isRequired>Copy</Label>
                      <FormattedCopyField value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                      <p className="text-xs text-tertiary">Use - for bullets and **text** for bold.</p>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="linkText"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input label="Link display text" placeholder="e.g. Learn more" {...field} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="linkUrl"
                    rules={{
                      validate: (value) => {
                        if (!value || !value.trim()) return true;
                        const v = value.trim();
                        if (v.startsWith("/")) return true;
                        try {
                          new URL(v);
                          return true;
                        } catch {
                          return "Must be a valid URL or a relative path starting with /";
                        }
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          label="Link URL"
                          placeholder="https://... or /page-path"
                          isInvalid={!!fieldState.error}
                          hint={fieldState.error?.message}
                          {...field}
                        />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-3 rounded-lg bg-secondary p-4">
                  <Toggle
                    size="md"
                    isSelected={form.watch("showAsPopup")}
                    onChange={(checked) => form.setValue("showAsPopup", checked)}
                    label="Show as pop-up notification"
                    hint="Auto-displays in the bottom left when users first visit the app. Shows once per user for 30 days from publish date."
                    className="[&>div>p]:text-sm! [&>div>span]:text-sm! [&>div>span]:text-wrap [&>div>span]:max-w-[500px]"
                  />
                  <Toggle
                    size="md"
                    isSelected={form.watch("isPublished")}
                    onChange={(checked) => form.setValue("isPublished", checked)}
                    label="Publish update"
                    className="[&>div>p]:text-sm!"
                  />
                </div>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={onClose}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" type="submit">
                  Save Update
                </Button>
              </div>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
